import React, { useCallback, useEffect } from 'react'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import type { Address } from '@towns-protocol/web3'
import { Box, Button, Stack, Text } from '@ui'
import { type GetSigner, WalletReady } from 'privy/WalletReady'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { useBotData, useUninstallApp } from 'use-towns-client'

export const UninstallBotModal = (props: {
    onOpenChange: (open: boolean) => void
    open: boolean
    appAddress: Address
    spaceAddress: string
    onSuccess?: () => void
    onError?: () => void
}) => {
    const { onOpenChange, open, appAddress, spaceAddress, onSuccess, onError } = props

    const { execute: uninstallApp, isLoading, isSuccess, isError } = useUninstallApp()
    const { data: botData } = useBotData(appAddress)

    const onConfirm = useCallback(
        async (getSigner: GetSigner) => {
            if (!botData) {
                return
            }
            const signer = await getSigner()
            if (!signer) {
                createPrivyNotAuthenticatedNotification()
                return
            }
            await uninstallApp({
                target: 'space',
                spaceAddress: spaceAddress as Address,
                appAddress: botData.appAddress as Address,
                appUserId: botData.clientAddress,
                signer,
            })
        },
        [botData, spaceAddress, uninstallApp],
    )

    useEffect(() => {
        if (isSuccess) {
            onSuccess?.()
            onOpenChange(false)
        }
    }, [isSuccess, onOpenChange, onSuccess])

    useEffect(() => {
        if (isError) {
            onError?.()
            onOpenChange(false)
        }
    }, [isError, onOpenChange, onError])

    if (!open) {
        return null
    }

    return (
        <ModalContainer onHide={() => onOpenChange(false)}>
            <Stack padding="sm" gap="lg" alignItems="start">
                <Text fontWeight="strong">Uninstall Bot</Text>
                <Text>Are you sure you want to uninstall this bot?</Text>
                <Stack horizontal gap width="100%">
                    <Box grow />
                    <Button tone="level2" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <WalletReady>
                        {({ getSigner }) => (
                            <>
                                <Button
                                    tone="negative"
                                    disabled={isLoading}
                                    onClick={() => onConfirm(getSigner)}
                                >
                                    {isLoading ? <ButtonSpinner /> : 'Uninstall'}
                                </Button>
                            </>
                        )}
                    </WalletReady>
                </Stack>
            </Stack>
        </ModalContainer>
    )
}
